/**
 * A guided step that points at a frame's card scrolls the card into view
 * first, then points (Part 06 Q2). A frame open in focus or in a floating
 * window is already in view; the canvas keeps its camera.
 */
import { getWindowDisplayId } from "@/desktop/use-window-role";
import type { AgentPresentationStep } from "@/lib/agent-presentation-types";
import { frameOpenInWindow } from "@/lib/frame-screen-rect";
import { workspaceViewAdapter } from "@/lib/workspace-view-adapter";

/**
 * Brings the step's frame target into view; resolves once it is settled.
 * Undefined when there is nothing to bring in, so the step goes on at once.
 */
export function ensureFrameTargetsVisible(
  step: AgentPresentationStep,
  signal: AbortSignal,
): Promise<boolean> | undefined {
  const frameId =
    "frame_target" in step ? step.frame_target?.frame_id : undefined;
  if (!frameId || signal.aborted || frameOpenInWindow(frameId)) return;
  // Another display's window brings its own card into view.
  const displayId =
    "target_display_id" in step ? step.target_display_id : undefined;
  if (displayId !== undefined && displayId !== getWindowDisplayId()) return;
  return workspaceViewAdapter().bringIntoView(frameId, signal);
}
